import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, ArrowRight } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { AaiNotification, AaiNotificationKind } from '../../types/aai';
import { Loading, ErrorNote } from './components';
import { PageHeader } from './dashboards/ui';

// A small icon/tint per kind so the inbox scans quickly. Approvals-needed are
// amber (action on you); resolved outcomes are neutral/green/red.
const KIND_STYLE: Record<AaiNotificationKind, { dot: string; tag: string }> = {
  request_submitted:      { dot: 'bg-amber-400',   tag: 'Needs review' },
  request_awaiting_final: { dot: 'bg-amber-400',   tag: 'Needs approval' },
  request_approved:       { dot: 'bg-emerald-500', tag: 'Approved' },
  request_declined:       { dot: 'bg-red-500',     tag: 'Declined' },
  request_needs_info:     { dot: 'bg-amber-400',   tag: 'More info' },
  skill_access_requested: { dot: 'bg-amber-400',   tag: 'Access request' },
  skill_access_granted:   { dot: 'bg-emerald-500', tag: 'Access granted' },
  skill_access_denied:    { dot: 'bg-red-500',     tag: 'Access denied' },
  skill_share_proposed:   { dot: 'bg-amber-400',   tag: 'Share review' },
  request_building:       { dot: 'bg-brand-500',   tag: 'Being built' },
  tool_delivered:         { dot: 'bg-emerald-500', tag: 'Delivered' },
  tool_announced:         { dot: 'bg-brand-500',   tag: 'Now allowed' },
  prd_review_requested:   { dot: 'bg-amber-400',   tag: 'PRD review' },
  prd_changes_requested:  { dot: 'bg-amber-400',   tag: 'Changes asked' },
  prd_approved_by_expert: { dot: 'bg-emerald-500', tag: 'Cleared to build' },
  lesson_invite:          { dot: 'bg-brand-500',   tag: 'Your next lesson' },
  session_scheduled:      { dot: 'bg-brand-500',   tag: 'Session scheduled' },
  control_request:        { dot: 'bg-amber-400',   tag: 'Asked of you' },
  portfolio_recommendation: { dot: 'bg-amber-400', tag: 'Portfolio decision' },
  portfolio_response:     { dot: 'bg-emerald-500', tag: 'Owner answered' },
  collection_awaiting_release: { dot: 'bg-amber-400', tag: 'Release decision' },
  collection_decided:     { dot: 'bg-emerald-500', tag: 'Owner decided' },
  quote_sent:             { dot: 'bg-amber-400',   tag: 'Quote to accept' },
  request_built:          { dot: 'bg-brand-500',   tag: 'Build ready' },
  information_requested:  { dot: 'bg-amber-400',   tag: 'Handed to you' },
  connection_requested:   { dot: 'bg-amber-400',   tag: 'Connection to approve' },
};

const canOpen = (link: string | null): boolean => {
  if (!link || !link.startsWith('/')) return false;
  return !link.startsWith('/partner/');
};

const timeAgo = (iso: string | null): string => {
  if (!iso) return '';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AaiNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await aaiApi.listNotifications({ limit: 100 })).data.notifications); setError(null); }
    catch { setError('Could not load notifications.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const unread = items.filter((n) => !n.read).length;

  const markAll = async () => {
    if (busy || unread === 0) return;
    setBusy(true);
    try {
      await aaiApi.markNotificationsRead();
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      window.dispatchEvent(new Event('aai:notifications-changed'));
    } catch { setError('Could not update notifications.'); }
    finally { setBusy(false); }
  };

  // Click a row to mark it read and, when the notification carries an in-app
  // link (DEL-3: a scheduled session → /my-sessions, a lesson invite → the
  // lesson), follow it. Rows without a link are inert once read.
  const markOne = async (n: AaiNotification) => {
    if (n.read) return;
    try {
      await aaiApi.markNotificationsRead([n.id]);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      window.dispatchEvent(new Event('aai:notifications-changed'));
    } catch { setError('Could not update notifications.'); }
  };
  // /partner/* only resolves in the partner console; this page is the company
  // shell's, so a row carrying a partner link says what happened and stops there.
  const openOne = (n: AaiNotification) => {
    markOne(n);
    if (canOpen(n.link)) navigate(n.link as string);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <PageHeader icon={<Bell size={20} className="text-brand-500" />} title="Notifications"
        subtitle="Requests, approvals and skill access — everything waiting on you." />

      <div className="flex items-center justify-between mb-3">
        <span className="text-[12.5px] text-gray-500">
          {unread > 0 ? `${unread} unread` : 'All caught up'}
        </span>
        <button onClick={markAll} disabled={busy || unread === 0}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-600 hover:text-gray-900 disabled:opacity-40">
          <CheckCheck size={15} /> Mark all read
        </button>
      </div>

      {loading ? <Loading /> : error ? <ErrorNote message={error} /> : (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          {items.length === 0 && (
            <p className="px-5 py-10 text-center text-[13px] text-gray-400">No notifications yet.</p>
          )}
          {items.map((n) => {
            const s = KIND_STYLE[n.kind] ?? { dot: 'bg-gray-300', tag: '' };
            const hasLink = canOpen(n.link);
            return (
              <button key={n.id} onClick={() => openOne(n)} disabled={n.read && !hasLink}
                className={`w-full text-left px-5 py-3.5 border-b border-gray-50 last:border-0 flex gap-3 transition-colors ${n.read ? (hasLink ? 'hover:bg-gray-50' : 'cursor-default') : 'bg-brand-50/40 hover:bg-brand-50'}`}>
                <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? 'bg-transparent' : s.dot}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-[14px] text-gray-900 ${n.read ? 'font-medium' : 'font-semibold'}`}>{n.title}</span>
                    {s.tag && <span className="text-[10px] uppercase tracking-wider text-gray-400">{s.tag}</span>}
                  </div>
                  {n.body && <div className="text-[12.5px] text-gray-500 mt-0.5">{n.body}</div>}
                  <div className="text-[11px] text-gray-400 mt-1">{timeAgo(n.created_at)}</div>
                </div>
                {hasLink && <ArrowRight size={15} className="text-gray-300 shrink-0 mt-1" />}
                {n.read && !hasLink && <Check size={15} className="text-gray-300 shrink-0 mt-1" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
